import React, { useState, useEffect, useCallback } from 'react';
import { documentsAPI } from '../api/client';
import { useClientConfig } from '../api/clientConfig';
import { extractErrorMessage } from '../utils/errors';

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * The N-many generalisation of the legacy single-file `file` field on
 * DocumentForm -- e.g. a policy with three referenced procedure files.
 *
 * Requires an existing document id (POST /{doc_id}/attachments), so this only
 * ever renders on the edit and view pages, never on /documents/new -- a
 * document must exist before anything can be attached to it.
 *
 * `editable` gates upload/delete, not just visibility: the backend rejects
 * both once the document leaves DRAFT, so this mirrors that rather than
 * showing controls that would just 400.
 *
 * `revisionId`, when set, switches from the live attachment set to the
 * proposed one -- live rows plus whatever's been staged or marked for
 * removal on that revision (GitHub issue #157). Three row states, driven
 * entirely by the `pending_removal`/`revision_id` fields the backend
 * already computes from DocumentAttachment's own (is_active, revision_id)
 * state -- see that model's docstring:
 *
 *   - STAGED (revision_id set, pending_removal false): a new file, not yet
 *     live. "Pending" badge; Remove undoes the add outright.
 *   - PENDING REMOVAL (pending_removal true): an EXISTING live attachment
 *     marked for removal. "Pending removal" badge; Restore undoes it -- the
 *     row is still fully live (still downloadable) until the revision
 *     publishes or is withdrawn.
 *   - plain live row: on a revision-aware panel, Remove now STAGES a
 *     removal rather than deleting outright (unlike the plain,
 *     non-revision panel, where Remove is still immediate).
 *
 * All three call the SAME deleteAttachment endpoint -- the backend branches
 * on the row's own state, so this component never needs to say which case
 * it means.
 */
function AttachmentsPanel({ documentId, revisionId, editable }) {
  const { max_upload_mb: maxUploadMb } = useClientConfig();

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAttachments = useCallback(async () => {
    try {
      const res = await documentsAPI.listAttachments(documentId, revisionId);
      setAttachments(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [documentId, revisionId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      await documentsAPI.addAttachments(documentId, files, revisionId);
      await fetchAttachments();
    } catch (err) {
      console.error('Error uploading attachments:', err);
      setError(extractErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const res = await documentsAPI.downloadAttachment(documentId, attachment.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download file');
      console.error('Attachment download error:', err);
    }
  };

  // One handler for all three row actions (undo a staged add, stage a
  // removal, undo a pending removal) -- the backend's delete_attachment
  // branches entirely on the row's own state, so the request is identical
  // either way. `confirmText` only changes what the user is asked.
  const handleDelete = async (attachment, confirmText) => {
    if (!window.confirm(confirmText)) return;
    try {
      await documentsAPI.deleteAttachment(documentId, attachment.id);
      await fetchAttachments();
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError(extractErrorMessage(err));
    }
  };

  if (loading) return null;
  // Nothing to show on the read-only view page for a document with none --
  // an empty "Attachments" card would just be noise there. The edit page
  // still shows the card (with its upload control) even when empty.
  if (!editable && attachments.length === 0) return null;

  return (
    <div className="card">
      <div className="card-header"><h3>📎 Attachments ({attachments.length})</h3></div>
      {error && <div className="alert alert-error">{error}</div>}

      {attachments.length === 0 ? (
        <div className="empty-state"><p>No attachments.</p></div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>File</th>
                <th>Size</th>
                <th>Uploaded By</th>
                <th>Uploaded At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {attachments.map((a) => {
                // GitHub issue #157: three row states instead of two -- see
                // the component docstring. isPendingRemoval must be checked
                // FIRST: a pending-removal row also carries a non-null
                // revision_id, so testing revision_id alone would
                // misclassify it as "staged".
                const isPendingRemoval = a.pending_removal;
                const isStaged = a.revision_id != null && !isPendingRemoval;
                // Unlike before issue #157, EVERY row now has some action
                // once editable, on both panel shapes -- a plain live row
                // used to have no affordance at all on a revision-aware
                // panel; now Remove stages a removal instead. Only the
                // label/confirm text and badge differ by row state.
                const canAct = editable;
                let actionLabel = '🗑️ Remove';
                let confirmText = `Remove ${a.original_filename}?`;
                if (isPendingRemoval) {
                  actionLabel = '↩️ Restore';
                  confirmText = `Restore ${a.original_filename} so it is no longer marked for removal?`;
                } else if (!isStaged && revisionId != null) {
                  confirmText = `Mark ${a.original_filename} for removal when this revision publishes?`;
                }
                return (
                  <tr key={a.id}>
                    <td>
                      {a.original_filename}
                      {isStaged && (
                        <span className="badge" style={{ marginLeft: '8px', background: 'var(--info-bg)', color: 'var(--info-ink)' }}>
                          Pending
                        </span>
                      )}
                      {isPendingRemoval && (
                        <span className="badge" style={{ marginLeft: '8px', background: 'var(--warning-bg)', color: 'var(--warning-ink)' }}>
                          Pending removal
                        </span>
                      )}
                    </td>
                    <td>{formatSize(a.size_bytes)}</td>
                    <td>{a.uploaded_by_name || '—'}</td>
                    <td>{new Date(a.uploaded_at).toLocaleString()}</td>
                    <td>
                      <button
                        className="btn btn-secondary btn-small"
                        onClick={() => handleDownload(a)}
                        style={{ marginRight: '5px' }}
                      >
                        ⬇️ Download
                      </button>
                      {canAct && (
                        <button
                          className={isPendingRemoval ? 'btn btn-secondary btn-small' : 'btn btn-danger btn-small'}
                          onClick={() => handleDelete(a, confirmText)}
                        >
                          {actionLabel}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editable && (
        <div style={{ marginTop: '14px' }}>
          <input
            type="file"
            multiple
            onChange={handleUpload}
            disabled={uploading}
            accept=".pdf,.docx,.doc,.txt,.xlsx,.pptx,.md"
          />
          {uploading && <span style={{ marginLeft: '10px', color: 'var(--muted)' }}>⏳ Uploading...</span>}
          <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--muted)' }}>
            Allowed: PDF, DOCX, DOC, TXT, XLSX, PPTX, MD (max {maxUploadMb}MB each). Select multiple files at once to attach several.
          </div>
        </div>
      )}
    </div>
  );
}

export default AttachmentsPanel;
